import { datadogRum } from '@datadog/browser-rum';
import { TZDate } from '@date-fns/tz';
import { format } from 'date-fns';

// BL-654: shared by the V1 CX block and the V2 buy box block while both run
// side by side. Hours are not what the A/B varies, so a divergence here would
// be a bug in one variant and a broken comparison in both.
export const DEFAULT_TIME_ZONE = 'America/New_York';

// BL-409: only a configured schedule can close a channel. Missing hours meant
// the CTA was disabled all day — cx_hours has no Friday, so chat and call were
// dead every Friday during business hours, which is the real damage here. A
// content gap should not silently remove a working support channel: leave the
// CTA live and let the chat widget and the phone line speak for themselves.
export const isScheduleUnavailable = ({ isConfigured, isAvailableToday, isAvailableNow }) =>
  isConfigured ? !isAvailableNow || !isAvailableToday : false;

export const getTimesText = ({ isConfigured, isAvailableToday, times }) => {
  // BL-409: with no hours configured for today we do not know whether the team
  // is open, so we say nothing rather than asserting "Unavailable".
  if (!isConfigured) return '';
  if (!isAvailableToday) return 'Unavailable';
  const tz = typeof times?.tz === 'string' ? times.tz : '';
  return `${times?.from} - ${times?.to}${tz ? ` ${tz}` : ''}`;
};

// BL-943: the visitor's time zone and locale come from the browser, and some
// environments hand back values Intl rejects (an empty string, "Etc/Unknown").
// Used as-is they made the formatting below throw, and the catch reported each
// throw to Datadog. Validated once per value, since the widgets ask again on
// every render.
const timeZoneValidity = new Map();

const isValidTimeZone = (timeZone) => {
  if (typeof timeZone !== 'string' || !timeZone) return false;
  if (!timeZoneValidity.has(timeZone)) {
    let valid = true;
    try {
      new Intl.DateTimeFormat('en-US', { timeZone });
    } catch {
      valid = false;
    }
    timeZoneValidity.set(timeZone, valid);
  }
  return timeZoneValidity.get(timeZone);
};

// A bad locale only affects the "EST"-style label, so fall back to en-US
// rather than hide the hours.
const toSupportedLocale = (locale) => {
  try {
    return Intl.getCanonicalLocales(locale)[0] || 'en-US';
  } catch {
    return 'en-US';
  }
};

// Returns availability and display times for today's CX schedule.
// schedule: array (Contentstack cxHours) or object (keyed by day name)
// displayTimeZone: the user's local timezone for formatting output times
// type: 'chat' | 'call' — selects the right sub-schedule when using the array format
// displayLocale: user's locale for extracting the short timezone label (e.g. "EST", "BST")
// componentName: tags reported errors so RUM can tell the two variants apart
export function getTodaysSchedule(
  schedule,
  displayTimeZone = DEFAULT_TIME_ZONE,
  type = 'chat',
  displayLocale = 'en-US',
  componentName = 'ContactCX'
) {
  // Schedule hours are always stored in Eastern time regardless of the user's location
  const scheduleTimeZone = DEFAULT_TIME_ZONE;

  // Accepts "HH:MM" strings or pre-parsed { hour, minute } objects
  const parseTime = (time) => {
    if (!time) return null;
    if (typeof time === 'object' && 'hour' in time) return time;
    if (typeof time === 'string') {
      const [hour, minute] = time.split(':').map(Number);
      return { hour, minute };
    }
    return null;
  };
  const hasValidTime = (time) =>
    !!time &&
    Number.isInteger(time.hour) && time.hour >= 0 && time.hour <= 23 &&
    Number.isInteger(time.minute) && time.minute >= 0 && time.minute <= 59;

  const unavailable = {
    isConfigured: true,
    isAvailableToday: false,
    isAvailableNow: false,
    times: { from: null, to: null, tz: '' },
  };

  // Same shape, but "we have no hours for this day" rather than "closed today".
  const unconfigured = { ...unavailable, isConfigured: false };

  if (!schedule) return unconfigured;

  // No usable time zone means no times we can show: unconfigured hides them
  // and keeps the CTA live, without reporting anything.
  if (!isValidTimeZone(displayTimeZone)) return unconfigured;
  const locale = toSupportedLocale(displayLocale);

  const days = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
  ];

  // Anchor "now" to the schedule timezone so day boundaries match business hours
  const now = new Date();
  const nowTZ = new TZDate(now, scheduleTimeZone);

  // BL-943: an environment that replaces the global `Date` before our bundle
  // loads makes `class TZDate extends Date` build plain Dates with none of
  // TZDate's methods (reported for GoogleBot sessions). Every call below would
  // throw and the catch would report "withTimeZone is not a function" to
  // Datadog. Nothing here can be computed in that environment, so fall back
  // quietly.
  if (!(nowTZ instanceof TZDate)) return unconfigured;

  const dayName = days[nowTZ.getDay()];

  // Support two schedule shapes: array of day objects (cxHours) or a plain day-keyed object.
  // `name` is optional-chained because this lookup sits outside the try/catch
  // below: a row without one would throw here and take the whole block down.
  const dayEntry = Array.isArray(schedule)
    ? schedule.find((d) => d?.name?.toLowerCase() === dayName)
    : schedule?.[dayName];
  const dayData = Array.isArray(schedule) ? dayEntry?.[type] : dayEntry;

  // BL-409: a day the CMS never configured is not the same as a day the team
  // chose to close. Both used to land here and render "Unavailable", so a
  // content gap was indistinguishable from a business decision — cx_hours has
  // no Friday entry, and that read as "closed every Friday" for weeks.
  if (!dayData) {
    return unconfigured;
  }

  if (!dayData.available) {
    return unavailable;
  }

  const fromTime = parseTime(dayData.from || dayData.start_time);
  const toTime = parseTime(dayData.to || dayData.end_time);

  if (!hasValidTime(fromTime) || !hasValidTime(toTime)) {
    return unavailable;
  }

  // Build a TZDate for today at a given hour/minute; dateOffset handles overnight windows (e.g. to < from)
  const makeTZDate = ({ hour, minute }, dateOffset = 0) =>
    new TZDate(nowTZ.getFullYear(), nowTZ.getMonth(), nowTZ.getDate() + dateOffset, hour, minute, 0, scheduleTimeZone);

  // BL-488: 24 users hit "p.withTimeZone is not a function" in a single burst
  // on Aug 3, never again in the three weeks since on the same code — points
  // to a moment of half-saved cx_hours in Contentstack, not a standing bug.
  // Cheap insurance: a schedule we can't compute should read as unconfigured
  // (silent, CTA stays live), not crash the block for every visitor hitting
  // that content window.
  try {
    const from         = makeTZDate(fromTime);
    const to           = makeTZDate(toTime);
    const normalizedTo = to < from ? makeTZDate(toTime, 1) : to; // push end to next day if window crosses midnight

    // Convert the schedule window to the user's local timezone for display
    const displayFrom = from.withTimeZone(displayTimeZone);
    const displayTo = normalizedTo.withTimeZone(displayTimeZone);

    const isAvailableNow = nowTZ >= from && nowTZ <= normalizedTo;

    const timeFormat = 'h:mm a';

    // Extract the short timezone abbreviation (e.g. "EST", "PST") for the user's locale
    const tz = new Intl.DateTimeFormat(locale, {
      timeZone: displayTimeZone,
      timeZoneName: 'short',
    })
      .formatToParts(displayFrom)
      .find((part) => part.type === 'timeZoneName')?.value ?? '';

    return {
      isConfigured: true,
      isAvailableToday: true,
      isAvailableNow,
      times: {
        from: format(displayFrom, timeFormat),
        to: format(displayTo, timeFormat),
        tz,
      },
    };
  } catch (error) {
    // Report before falling back — an unconfigured schedule keeps the CTA
    // live and silent, but we still want to know if this keeps happening.
    datadogRum.addError(error, { component: componentName, function: 'getTodaysSchedule' });
    return unconfigured;
  }
}
